/** The Market Outlook page's data shaping (screens design §2.3): one chart model per series and the "Your markets" cells. Pure; reads only the `MarketOutlook` DTO. */
import type { ContentCatalog } from "@bsim/contracts";
import type { MarketOutlook } from "@bsim/domain";
import { formatIndexLevel, formatRate, quarterLabel } from "../format";
import { BOOK_LABELS } from "../financials/statements";
import type { ChartTable } from "../theme/ChartFrame";

type Series = MarketOutlook["series"][number];
export type SeriesId = Series["id"];
export const HORIZONS = [12, 24, 36] as const;
export type Horizon = (typeof HORIZONS)[number];

/** The page's cards and the series each draws, in order. */
export const SECTIONS: readonly { readonly key: string; readonly title: string; readonly ids: readonly SeriesId[] }[] = [
  { key: "rates", title: "Rates", ids: ["policyRate", "ust2y", "ust10y"] },
  { key: "economy", title: "Economy", ids: ["unemployment", "gdpGrowth", "cpi"] },
  { key: "property", title: "Property", ids: ["hpi", "creIndex", "officeIndex", "officeVacancy"] },
];

export const BOOKS: readonly { readonly key: string; readonly label: string }[] = [
  { key: "ci", label: BOOK_LABELS["ci"] ?? "Business loans" }, { key: "cre", label: BOOK_LABELS["cre"] ?? "Commercial property loans" }, { key: "resi", label: "Mortgages" },
  { key: "consumer", label: "Consumer" }, { key: "card", label: "Card" },
];

export const monthLabel = (catalog: ContentCatalog, month: number): string => (month === 0 ? "Start" : quarterLabel(catalog, month));

const pct = (v: number, signed = false): string => formatRate(v, 1, { signed });
const level = (s: Series, v: number): string => (s.unit === "rate" ? pct(v) : formatIndexLevel(v, 0));
/** An axis value in the chart's own scale (percent for a rate, the index as it is). */
const axisValue = (s: Series, v: number, dp: number): string => (s.unit === "rate" ? formatRate(v / 100, dp) : formatIndexLevel(v, dp));
/** Rates draw in percent, indices as they are. */
const scaleOf = (s: Series): number => (s.unit === "rate" ? 100 : 1);

export interface ChartModel {
  readonly id: SeriesId; readonly title: string; readonly unit: Series["unit"];
  readonly categories: readonly string[]; readonly months: readonly number[];
  /** Realised then consensus points, one per category. */
  readonly values: readonly number[];
  /** Index of the last realised point: the line turns dashed here and the "today" rule stands on it. */
  readonly todayAt: number;
  readonly range: { readonly start: number; readonly low: readonly number[]; readonly high: readonly number[] } | null;
  readonly yTicks: readonly number[];
  readonly formatY: (v: number) => string;
  readonly formatEnd: (v: number) => string;
  readonly table: ChartTable;
  readonly summary: string;
}

/** Ticks of a round step covering [lo, hi] (at least two). */
export function niceTicks(lo: number, hi: number): number[] {
  const raw = Math.max(hi - lo, 1e-6) / 3;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)) ?? raw;
  const first = Math.ceil(lo / step - 1e-9);
  const last = Math.floor(hi / step + 1e-9);
  return Array.from({ length: Math.max(last - first + 1, 2) }, (_, i) => Number(((first + i) * step).toFixed(6)));
}

export function chartOf(s: Series, outlook: MarketOutlook, horizon: Horizon, catalog: ContentCatalog): ChartModel {
  const k = scaleOf(s);
  const realised = s.realised.filter((p) => p.month <= outlook.realisedThroughMonth);
  const ahead = s.consensus.filter((p) => p.month <= outlook.asOfMonth + horizon);
  const months = [...realised.map((p) => p.month as number), ...ahead.map((p) => p.month as number)];
  const values = [...realised.map((p) => p.value * k), ...ahead.map((p) => p.point * k)];
  const todayAt = realised.length - 1;
  const last = realised[todayAt];
  const hasRange = ahead.some((p) => p.high > p.low) && last !== undefined;
  const range = hasRange && last !== undefined
    ? { start: todayAt, low: [last.value * k, ...ahead.map((p) => p.low * k)], high: [last.value * k, ...ahead.map((p) => p.high * k)] }
    : null;
  const ticks = niceTicks(Math.min(...values, ...(range?.low ?? [])), Math.max(...values, ...(range?.high ?? [])));
  const decimals = ticks.every((t) => Number.isInteger(t)) ? 0 : 1;
  const word = (m: number) => monthLabel(catalog, m);
  return {
    id: s.id, title: s.label, unit: s.unit, months, values, todayAt, range,
    categories: months.map(word),
    yTicks: ticks,
    formatY: (v) => axisValue(s, v, decimals),
    formatEnd: (v) => axisValue(s, v, s.unit === "rate" ? 1 : 0),
    summary: `${s.label}: realised to ${word(outlook.realisedThroughMonth)}, then the consensus with its range to ${word(months.at(-1) ?? 0)}`,
    table: {
      caption: `${s.label}: realised and consensus by quarter-end`,
      head: ["Quarter-end", "Realised", "Consensus", "Range"],
      rows: [
        ...realised.map((p) => [word(p.month), level(s, p.value), "–", "–"]),
        ...ahead.map((p) => [word(p.month), "–", level(s, p.point), p.high > p.low ? `${level(s, p.low)} to ${level(s, p.high)}` : "–"]),
      ],
    },
  };
}

/** "+3.5% (2.0% to 5.0%)": the consensus point with its range where one is published. */
export function cellOf(s: Series | undefined, outlook: MarketOutlook, horizon: Horizon, signed: boolean): string | null {
  const p = s?.consensus.find((c) => c.month === outlook.asOfMonth + horizon);
  if (p === undefined) return null;
  return p.high > p.low ? `${pct(p.point, signed)} (${pct(p.low)} to ${pct(p.high)})` : pct(p.point, signed);
}

/** What a consensus move means for a bank, in the words of the Results economy strip (results/economy.ts EFFECT), turned forward: the series' direction to the effect. A series with no entry gets the consensus line alone. */
const SO_WHAT: Readonly<Partial<Record<SeriesId, { readonly up: string; readonly down: string; /** Said when the series stays near where it is (only the two economy lines that have no other way to read). */ readonly flat?: string }>>> = {
  policyRate: { up: "deposit costs rise", down: "deposit costs ease" },
  ust10y: { up: "bond values fall", down: "bond values rise" },
  unemployment: { up: "more borrowers struggle", down: "fewer borrowers struggle" },
  gdpGrowth: { up: "loan demand strengthens", down: "loan demand weakens", flat: "loan demand holds steady" },
  cpi: { up: "rate cuts are less likely", down: "pressure on rates eases", flat: "rate pressure holds steady" },
  hpi: { up: "collateral values rise", down: "collateral values fall" },
  creIndex: { up: "collateral values rise", down: "collateral values fall" },
  officeIndex: { up: "collateral values rise", down: "collateral values fall" },
  officeVacancy: { up: "more office borrowers struggle", down: "fewer office borrowers struggle" },
};

/**
 * The one "so what" line under a chart: "Consensus: policy rate falls from 3.5% to 3.0% by Q4 FY2027. What it usually means for banks: deposit costs ease." The move is read on the figures the chart prints (a change under their last place is "holds near");
 * the effect clause only where the series has one.
 */
export function soWhatLine(m: ChartModel): string | null {
  const from = m.values[m.todayAt], to = m.values.at(-1);
  if (from === undefined || to === undefined || m.values.length - 1 <= m.todayAt) return null;
  const [a, b] = [m.formatEnd(from), m.formatEnd(to)];
  const name = m.title.replace(/^[A-Z](?![A-Z0-9])/, (c) => c.toLowerCase());
  const when = m.categories.at(-1) ?? "";
  const flat = a === b;
  // "house prices rise", "the policy rate rises": the verb follows the name's number (a plural name ends in a single s).
  const plural = /[^s]s$/.test(name);
  const [stays, rises, falls] = plural ? ["stay", "rise", "fall"] : ["stays", "rises", "falls"];
  const effect = flat ? SO_WHAT[m.id]?.flat : SO_WHAT[m.id]?.[to > from ? "up" : "down"];
  return `Consensus: ${name} ${flat ? `${stays} near ${a}` : `${to > from ? rises : falls} from ${a} to ${b}`} by ${when}.${effect === undefined ? "" : ` What it usually means for banks: ${effect}.`}`;
}
